import { logger } from "../helpers/logger";
import { CartSlice } from "../types/Cart";

/** Key in localStorage */
const STORAGE_KEY = "cartSlice";
/** Used for automatically flushing the localStorage when a new build is deployed. */
const VERSION_TOKEN = `v${__APP_VERSION__}`;

/**
 * Loads the cart slice from localStorage.
 *
 * @returns {CartSlice} - The cart slice from localStorage or an empty object if it fails to load.
 */
export const loadState = (): CartSlice => {
  try {
    const serializedState = localStorage.getItem(STORAGE_KEY);
    if (!serializedState) {
      saveState({} as CartSlice);
      return loadState();
    }

    const state = JSON.parse(serializedState) as CartSlice;
    if (state._version !== VERSION_TOKEN) {
      localStorage.removeItem(STORAGE_KEY);
      saveState({} as CartSlice);
      return loadState();
    }

    return state;
  } catch (e) {
    logger.error("Failed to load state from localStorage", e as Error);
    return {} as CartSlice;
  }
};

/**
 * Saves the cart slice to localStorage.
 *
 * @param {CartSlice} state - The cart slice to save to localStorage.
 */
export const saveState = (state: CartSlice) => {
  try {
    const stateWithVersion = {
      ...state,
      _version: VERSION_TOKEN,
    };
    const serializedState = JSON.stringify(stateWithVersion);
    localStorage.setItem(STORAGE_KEY, serializedState);
  } catch (e) {
    logger.error("Failed to save state to localStorage", e as Error);
  }
};
